import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

/**
 * Top-offset frame for the public Raises pages.
 *
 * The Raises routes (`/raises`, `/raises/directory`, `/raises/:slug`,
 * `/companies/:slug`, `/raises/:slug/claim`) are dual-layout:
 *  - Anonymous visitors render inside `PublicLayout`, whose `Navbar` is
 *    `fixed top-0` (~74px tall) and therefore OVERLAYS page content. Every other
 *    public page (InvestLanding, CaseStudies, …) clears it with `pt-[74px]`.
 *  - Signed-in users render inside `AuthenticatedLayout` (a sidebar, no fixed
 *    top bar), where that offset would create a wrong empty gap.
 *
 * This frame applies the navbar offset ONLY in the anonymous/public context, so
 * the page hero/header is never clipped behind the fixed navbar, and the
 * signed-in app-shell view stays flush. Detection mirrors the App layout split
 * (`!user` ⇒ PublicLayout for these non-public-only routes).
 */

export function RaisesPublicFrame({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  const isAnonymousRaisesLanding = !user && normalizedPath === '/raises';

  return (
    <div
      className={
        user
          ? undefined
          : isAnonymousRaisesLanding
            ? 'bg-[#f7f5f0] pt-[74px]'
            : 'pt-[74px]'
      }
    >
      {children}
    </div>
  );
}
